/* eslint-disable @typescript-eslint/no-explicit-any */

import { PayloadAction } from '@reduxjs/toolkit'
import { all, put, select, takeEvery } from 'redux-saga/effects'
import { MapDatasetEnriched } from 'features/builder/types'
import { buildAggregationExp, getDefaultAggregationExpForLayerType } from './spatialIndexUtils'
import {
  showGlobalBuilderError,
  updateSpatialIndexAggregationRequest,
  updateSpatialIndexAggregationResLevelRequest
} from './builderSlice'
import { getLayersByDatasetId, getEnrichedDatasetById } from './keplerGlSlice'
import { LayerPopupSettings } from '../types'
import { updateDataset } from './datasetSagas'

function* updateSpatialIndexAggregation(
  action: PayloadAction<string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<any, any, any> {
  try {
    const datasetId = action.payload
    const aggregationExp: string = yield getAggregationExp(datasetId)

    yield updateDataset(datasetId, { aggregationExp })
  } catch (error: any) {
    const msg = `Error at aggregation ${action.payload}. ${error.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

export function* getAggregationExp(datasetId: string, validFields?: string[]) {
  try {
    const dataset: MapDatasetEnriched | undefined = yield select(getEnrichedDatasetById, datasetId)
    const layers = yield select(getLayersByDatasetId, datasetId)
    const popupSettings = yield select((state) => state.builder.popupSettings?.layers) || []
    const popupSettingsByLayer = getPopupSettingsByLayer(popupSettings, layers)

    if (!dataset || !dataset.providerId) {
      throw new Error(`Error getting the aggregation expression: dataset not found}`)
    }
    return buildAggregationExp({
      layers,
      popupSettings: popupSettingsByLayer,
      provider: dataset.providerId,
      defaultDatasetAggregationExpressions: layers.map((l) =>
        getDefaultAggregationExpForLayerType(l.type, dataset.providerId, dataset.metadata?.schema)
      ),
      validFields
    })
  } catch (error) {
    throw new Error(`Error getting the aggregation expression ${(error as Error).message}`)
  }
}

function* updateSpatialIndexAggregationResLevel(
  action: PayloadAction<{ datasetId: string; aggregationResLevel: number }>
) {
  try {
    const { datasetId, aggregationResLevel } = action.payload

    yield updateDataset(datasetId, { aggregationResLevel })
  } catch (e: any) {
    const msg = `Error updating the aggregation resolution level. ${e.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

function* watchMap() {
  yield takeEvery(updateSpatialIndexAggregationRequest.type, updateSpatialIndexAggregation)
  yield takeEvery(updateSpatialIndexAggregationResLevelRequest.type, updateSpatialIndexAggregationResLevel)
}

export default function* spatialIndexSagas() {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  yield all([watchMap()])
}

function getPopupSettingsByLayer(
  popupSettings: Record<string, LayerPopupSettings> | undefined,
  layers
): Record<string, LayerPopupSettings> {
  return Object.fromEntries(Object.entries(popupSettings || {}).filter(([key]) => layers.find((l) => l.id === key)))
}
